import { useEffect, useState, useCallback } from 'react';
import { Table, Empty, Button, Space } from 'antd';
import { TableToolbar } from '../ui/TableToolbar.js';
import { SkeletonPage } from '../ui/SkeletonPage.js';
import { useUrlPagination } from '../hooks/useUrlPagination.js';
import { usePortalCtx } from '../context.js';
import { apiGet } from '../api.js';
import type { Paginated } from '../types/pagination.js';
import UpsertConfigDrawer, { type ConfigRow as Row } from './UpsertConfigDrawer.js';
import ConfigBulkImportDrawer from './ConfigBulkImportDrawer.js';
import ConfigExportMenu from './ConfigExportMenu.js';

interface ConfigRow {
  key: string;
  value: string;
  updatedAt: string;
  siteKey?: string;
}

const ENDPOINT = '/admin/config';

export default function ConfigList() {
  const { registryUrl, siteKey } = usePortalCtx();
  const { page, pageSize, q, setPage, setPageSize } = useUrlPagination();
  const [data, setData] = useState<Paginated<ConfigRow> | null>(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [editing, setEditing] = useState<Row | undefined>(undefined);
  const [importOpen, setImportOpen] = useState(false);

  const upsertLocal = useCallback((next: Row) => {
    setData((prev) => {
      const cur = prev ?? { items: [], total: 0 };
      const idx = cur.items.findIndex(
        (r) => r.key === next.key && (r.siteKey ?? '') === (next.siteKey ?? ''),
      );
      let items = cur.items.slice();
      if (idx >= 0) items[idx] = next;
      else items = [next, ...items];
      return { items, total: Math.max(cur.total, items.length) };
    });
  }, []);

  const deleteLocal = useCallback((id: { key: string; siteKey?: string }) => {
    setData((prev) => {
      if (!prev) return prev;
      const items = prev.items.filter(
        (r) => !(r.key === id.key && (r.siteKey ?? '') === (id.siteKey ?? '')),
      );
      return { items, total: Math.max(0, prev.total - 1) };
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const url = new URL(ENDPOINT, registryUrl);
    url.searchParams.set('page', String(page));
    url.searchParams.set('pageSize', String(pageSize));
    if (q) url.searchParams.set('q', q);
    if (siteKey) url.searchParams.set('siteKey', siteKey);
    (async () => {
      try {
        const resp = await apiGet<Paginated<ConfigRow>>(url.toString());
        if (!cancelled) setData(resp);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [registryUrl, siteKey, page, pageSize, q]);

  if (loading) return <SkeletonPage />;
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  if (!rows.length && !q) return <Empty description="No config entries yet" />;

  return (
    <>
      <TableToolbar
        placeholder="Search config"
        extra={
          <Space>
            <ConfigExportMenu rows={rows} filename="cmp-config" />
            <Button onClick={() => setImportOpen(true)}>Import</Button>
            <Button
              type="primary"
              onClick={() => {
                setMode('create');
                setEditing(undefined);
                setDrawerOpen(true);
              }}
            >
              Add Entry
            </Button>
          </Space>
        }
      />
      <Table<ConfigRow>
        rowKey={(r) => `${r.siteKey ?? 'global'}:${r.key}`}
        sticky
        size="small"
        dataSource={rows}
        columns={[
          { title: 'Key', dataIndex: 'key' },
          { title: 'Value', dataIndex: 'value' },
          { title: 'Scope', dataIndex: 'siteKey', render: (v?: string) => v ?? 'global' },
          {
            title: 'Updated',
            dataIndex: 'updatedAt',
            render: (v: string) => new Date(v).toLocaleString(),
          },
          {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, row: ConfigRow) => (
              <Space>
                <Button
                  size="small"
                  onClick={() => {
                    setMode('edit');
                    setEditing({
                      key: row.key,
                      value: row.value,
                      updatedAt: row.updatedAt,
                      siteKey: row.siteKey,
                    });
                    setDrawerOpen(true);
                  }}
                >
                  Edit
                </Button>
              </Space>
            ),
          },
        ]}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, s) => {
            setPage(p);
            if (s !== pageSize) setPageSize(s);
          },
        }}
      />

      <UpsertConfigDrawer
        open={drawerOpen}
        mode={mode}
        initial={editing}
        onClose={() => setDrawerOpen(false)}
        onSaved={upsertLocal}
        onDeleted={deleteLocal}
      />
      <ConfigBulkImportDrawer
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onUpsertLocal={(rowsImported) => {
          setData((prev) => {
            const cur = prev ?? { items: [], total: 0 };
            const map = new Map<string, ConfigRow>();
            for (const r of cur.items) map.set(`${r.siteKey ?? 'global'}:${r.key}`, r);
            for (const r of rowsImported)
              map.set(`${r.siteKey ?? 'global'}:${r.key}`, {
                key: r.key,
                value: r.value,
                siteKey: r.siteKey,
                updatedAt: new Date().toISOString(),
              });
            const items = Array.from(map.values());
            return { items, total: Math.max(cur.total, items.length) };
          });
        }}
      />
    </>
  );
}
